import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useAiBuilderActiveOperation,
  useOptionalAiBuilderSessionStore,
} from '../stores/AiBuilderSessionProvider';
import {
  type AiGenerateDockAttachedSource,
  type AiGenerateDockPreregisteredSource,
  type AiGenerateDockRegistrationView,
  preregisterAiGenerateDockSource,
} from '../utils/aiGenerateDockPreregistration';
import { type AiGenerateDockSource } from '../utils/aiGenerateDockRunInput';
import { type RegisterSource } from '../utils/serverAiBuilderChatTransport';

type Entry = {
  controller: AbortController;
  settled: boolean;
  /** 실행이 가져가 기다리는 등록 — 생성이 시작돼도 끊지 않는다. */
  claimed: boolean;
  registration: Promise<AiGenerateDockPreregisteredSource | null>;
};

/**
 * 붙어 있는 첨부마다 사전 등록 하나 — 첨부(`source` 객체 정체성)가 곧 등록의 키다.
 * - 새 첨부가 보이면 등록을 시작한다. 채팅·생성이 진행 중이면 미뤘다가 끝나면 시작한다
 *   (서버도 진행 중인 턴이 있으면 409 로 막는다).
 * - 등록할 수 없게 되면(꺼짐 — 게스트 등 / 채팅·생성 시작) 아직 진행 중인 등록은 끊는다. 같은
 *   서버 세션을 쓰는 턴과 겹치지 않게. 단 실행이 가져간 등록(`claimRegistration`)은 그 실행이
 *   기다리므로 남긴다. 끝난 등록도 그대로 두고, 다시 등록할 수 있게 되면 끊긴 첨부를 새로 등록한다.
 * - 사라지거나 바뀐 첨부의 등록은 끊는다. 교체·✕·입력 비우기·언마운트가 모두 같은 규칙이다.
 * - 개발 중 Fast Refresh 가 진행 중인 등록을 끊을 수 있다 — 그 첨부는 null 로 끝나 제출할 때
 *   다시 등록된다.
 * 반환값:
 * - `claimRegistration` — 제출 시점에 실행이 가져갈 등록. 가져간 등록은 끊지 않고, 다른 미완료 등록은
 *   그 자리에서 끊는다. 렌더에 쓰지 않으므로 ref 에 둔다.
 * - `registrationViewOf` — 칩이 보는 등록 상태(등록 중 / 등록됨+제목·길이). 렌더에 쓰므로 상태다.
 *   실패로 끝나면 없음(undefined)이다(조용히 — 제출할 때 다시 등록한다).
 */
export const useAiGenerateDockPreregistration = (
  attached: readonly AiGenerateDockAttachedSource[],
  {
    enabled = true,
    register,
  }: { enabled?: boolean; register?: RegisterSource } = {},
) => {
  const sessionStore = useOptionalAiBuilderSessionStore();
  const activeOperation = useAiBuilderActiveOperation();
  const entriesRef = useRef(new Map<AiGenerateDockSource, Entry>());
  const [views, setViews] = useState<
    ReadonlyMap<AiGenerateDockSource, AiGenerateDockRegistrationView>
  >(() => new Map());
  const canRegister = enabled && activeOperation === null;

  const setView = useCallback(
    (
      source: AiGenerateDockSource,
      view: AiGenerateDockRegistrationView | undefined,
    ) =>
      setViews(prev => {
        if (!view && !prev.has(source)) return prev;
        const next = new Map(prev);
        if (view) next.set(source, view);
        else next.delete(source);
        return next;
      }),
    [],
  );

  // 맞추기 — cleanup 이 아니라 본문에서 한다. 의존성이 바뀔 때마다 전부 끊지 않게.
  useEffect(() => {
    const entries = entriesRef.current;
    const current = new Set<AiGenerateDockSource>(
      attached.map(item => item.source),
    );

    entries.forEach((entry, source) => {
      if (
        current.has(source) &&
        (canRegister || entry.settled || entry.claimed)
      )
        return;
      entry.controller.abort();
      entries.delete(source);
      setView(source, undefined);
    });

    const port = sessionStore?.getPort();
    if (!port || !canRegister) return;
    attached.forEach(item => {
      if (entries.has(item.source)) return;
      const controller = new AbortController();
      const entry: Entry = {
        controller,
        settled: false,
        claimed: false,
        registration: preregisterAiGenerateDockSource(
          item,
          port,
          controller.signal,
          register,
        ),
      };
      entries.set(item.source, entry);
      setView(item.source, { status: 'registering' });
      void entry.registration.then(registered => {
        entry.settled = true;
        // 이미 빠진(또는 다시 붙은) 첨부의 옛 등록이면 표시를 건드리지 않는다.
        if (entries.get(item.source) !== entry) return;
        setView(
          item.source,
          registered
            ? {
                status: 'registered',
                title: registered.title,
                ...(registered.durationSeconds
                  ? { durationSeconds: registered.durationSeconds }
                  : {}),
              }
            : undefined,
        );
      });
    });
  }, [attached, canRegister, register, sessionStore, setView]);

  useEffect(() => {
    const entries = entriesRef.current;
    return () => entries.forEach(entry => entry.controller.abort());
  }, []);

  const claimRegistration = useCallback(
    (source: AiGenerateDockSource) => {
      const entries = entriesRef.current;
      // 실행이 곧 같은 세션으로 턴을 시작한다 — 다른 미완료 등록은 지금 끊는다. 작업 표시가 바뀐 뒤의 effect 를
      // 기다리면 그 사이 턴이 먼저 시작돼 같은 세션의 등록과 겹친다(SESSION_LOCKED). 끊긴 첨부는 실행이 끝나면
      // 위 맞추기가 다시 등록한다.
      entries.forEach((other, otherSource) => {
        if (otherSource === source || other.settled || other.claimed) return;
        other.controller.abort();
        entries.delete(otherSource);
        setView(otherSource, undefined);
      });
      const entry = entries.get(source);
      if (!entry) return undefined;
      entry.claimed = true;
      return entry.registration;
    },
    [setView],
  );
  const registrationViewOf = useCallback(
    (source: AiGenerateDockSource) => views.get(source),
    [views],
  );

  return { claimRegistration, registrationViewOf };
};
